import React, { useEffect, useMemo } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import PageContainer from "./layout/PageContainer.js";
import JsonLd from "./JsonLd.js";
import {
  COMMON_QUESTIONS_PAGE,
  buildCommonQuestionPageJsonLd,
  commonQuestionPath,
  getCommonQuestionById,
  getCommonQuestionNeighbors,
  getQuestionMetaDescription,
  getRelatedGuides,
} from "../constants/commonQuestions.js";
import "../mental-clarity-guide.css";

const DEFAULT_TITLE = "Ahavibes — Stop reacting. Start thinking clearly.";
const DEFAULT_DESCRIPTION =
  "A guided framework to examine interpretations, reduce mental noise, and make better decisions.";

const BRAIN_DUMP_HREF = "/?tab=brain-dump";

function ensureMeta(name, attribute = "name") {
  let el = document.querySelector(`meta[${attribute}="${name}"]`);
  const created = !el;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attribute, name);
    document.head.appendChild(el);
  }
  return { el, created, previous: el.getAttribute("content") };
}

const CommonQuestionPage = () => {
  const { questionId } = useParams();
  const question = getCommonQuestionById(questionId);
  const { prev, next } = getCommonQuestionNeighbors(questionId);
  const relatedGuides = useMemo(() => getRelatedGuides(question), [question]);

  const jsonLd = useMemo(
    () => (question ? buildCommonQuestionPageJsonLd(COMMON_QUESTIONS_PAGE, question) : null),
    [question]
  );

  useEffect(() => {
    if (!question) return;

    document.title = question.pageTitle || `${question.question} | Ahavibes`;

    const description = getQuestionMetaDescription(question);
    const descMeta = ensureMeta("description");
    descMeta.el.setAttribute("content", description);

    const keywordsMeta = ensureMeta("keywords");
    const keywordsValue = (question.keywords || []).join(", ");
    keywordsMeta.el.setAttribute("content", keywordsValue);

    const ogTitle = ensureMeta("og:title", "property");
    ogTitle.el.setAttribute("content", question.pageTitle || question.question);

    const ogDesc = ensureMeta("og:description", "property");
    ogDesc.el.setAttribute("content", description);

    const ogUrl = ensureMeta("og:url", "property");
    ogUrl.el.setAttribute("content", `https://ahavibes.xyz${commonQuestionPath(question.id)}`);

    window.scrollTo(0, 0);

    return () => {
      document.title = DEFAULT_TITLE;
      if (descMeta.created) descMeta.el.remove();
      else descMeta.el.setAttribute("content", descMeta.previous || DEFAULT_DESCRIPTION);
      if (keywordsMeta.created) keywordsMeta.el.remove();
      else if (keywordsMeta.previous != null) keywordsMeta.el.setAttribute("content", keywordsMeta.previous);
      else keywordsMeta.el.remove();
      if (ogTitle.created) ogTitle.el.remove();
      else if (ogTitle.previous != null) ogTitle.el.setAttribute("content", ogTitle.previous);
      if (ogDesc.created) ogDesc.el.remove();
      else if (ogDesc.previous != null) ogDesc.el.setAttribute("content", ogDesc.previous);
      if (ogUrl.created) ogUrl.el.remove();
      else if (ogUrl.previous != null) ogUrl.el.setAttribute("content", ogUrl.previous);
    };
  }, [question]);

  if (!question) {
    return <Navigate to={COMMON_QUESTIONS_PAGE.path} replace />;
  }

  return (
    <>
      {jsonLd ? <JsonLd data={jsonLd} id={`common-question-${question.id}`} /> : null}
      <PageContainer size="wide" className="mental-clarity-guide">
        <div className="common-questions common-questions--detail">
          <nav className="guide-section-nav guide-section-nav--top" aria-label="Guide navigation">
            <Link to={COMMON_QUESTIONS_PAGE.path} className="guide-section-nav__back">
              &larr; All common questions
            </Link>
          </nav>

          <article className="common-questions__detail">
            <p className="common-questions__eyebrow">{question.eyebrow}</p>
            <h1 className="common-questions__title">{question.question}</h1>

            <div className="common-questions__body">
              <div className="common-questions__copy">
                {question.answerParagraphs.map((paragraph) => (
                  <p key={paragraph.slice(0, 48)} className="common-questions__answer">
                    {paragraph}
                  </p>
                ))}

                {question.askYourself?.length ? (
                  <div className="common-questions__ask">
                    <h2 className="common-questions__ask-title">Ask yourself</h2>
                    <ul className="common-questions__ask-list">
                      {question.askYourself.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {question.tryAhavibes ? (
                  <p className="common-questions__try">
                    <strong>Try Ahavibes:</strong> {question.tryAhavibes}
                  </p>
                ) : null}

                {question.safetyNote ? (
                  <p className="common-questions__safety">{question.safetyNote}</p>
                ) : null}

                <div className="common-questions__actions">
                  <Link to={BRAIN_DUMP_HREF} className="primary-button common-questions__cta">
                    {question.ctaLabel} →
                  </Link>
                  {relatedGuides.map((guide) => (
                    <Link key={guide.path} to={guide.path} className="common-questions__guide-link">
                      Related guide: {guide.label}
                    </Link>
                  ))}
                </div>
              </div>
              <figure className="common-questions__figure">
                <img
                  src={question.illustration}
                  alt={question.illustrationAlt || ""}
                  className="common-questions__img"
                  width={420}
                  height={315}
                  loading="eager"
                  decoding="async"
                />
              </figure>
            </div>
          </article>

          <nav className="guide-section-nav" aria-label="Previous and next questions">
            <div className="guide-section-nav__links">
              {prev ? (
                <Link
                  to={commonQuestionPath(prev.id)}
                  className="guide-section-nav__link guide-section-nav__link--prev"
                >
                  &larr; {prev.question}
                </Link>
              ) : (
                <span />
              )}
              {next ? (
                <Link
                  to={commonQuestionPath(next.id)}
                  className="guide-section-nav__link guide-section-nav__link--next"
                >
                  {next.question} →
                </Link>
              ) : null}
            </div>
          </nav>
        </div>
      </PageContainer>
    </>
  );
};

export default CommonQuestionPage;
